import { Actions, Header, Image, Link, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { localyticsEvent, noop } from '../../../lib/utils';
import { ANALYTICS_EVENTS } from '../../constants';
import styles from './ThankYou.module.css';

const cx = classnames.bind(styles);

function renderContentWithGeneratedUsername(username, messageType) {
  localyticsEvent(ANALYTICS_EVENTS.RECEIVED_EDIT_USERNAME_THANK_YOU_PROMPT, {
    Context: messageType,
  });

  return (
    <React.Fragment>
      <Header as="h3">Thank You!</Header>
      <Text as="p">You&apos;ll be posting this {messageType} as:</Text>
      <Text as="p" bold>{username}</Text>
      <Text as="p">Not the catchiest username ever.<br />Edit it to make it your own.</Text>
    </React.Fragment>
  );
}

function renderContent(messageType, pointsAwarded) {
  const reviewContent = (
    <React.Fragment>
      <Header as="h3">Thanks for helping out</Header>
      <Text as="p">
        Your ratings will help improve this location and help the millions of GasBuddy users
      </Text>
      <br />
    </React.Fragment>
  );

  const priceContent = (
    <React.Fragment>
      <Header as="h3">Prices submitted, thank you!</Header>
      <div className={cx('successImageContainer')}>
        <Image
          alt="Submission Successful Trophy Icon"
          src="https://static.gasbuddy.com/web/consumer/submission-trophy.svg"
        />
      </div>
      {pointsAwarded > 0 && (
        <Text as="p">
          You have earned <Text as="strong">{pointsAwarded} points</Text> for those prices.
        </Text>
      )}
      <Text as="p">
        Every 1000 points for reporting prices earns you an entry into our daily prize draw to win $100 in FREE gas.
      </Text>
    </React.Fragment>
  );

  return messageType === 'review' ? reviewContent : priceContent;
}

export default function ThankYou({
  username,
  isGeneratedUsername,
  messageType,
  onDone,
  onEditUserName,
  pointsAwarded,
}) {
  const [showGeneratedUsernameContent, setShowGeneratedUsernameContent] = useState(isGeneratedUsername);

  const handleDoneClick = useCallback(() => {
    onDone();

    // If we were showing the username change content, then switch to the standard thank you content
    setShowGeneratedUsernameContent(false);
  }, [onDone]);

  return (
    <React.Fragment>
      { showGeneratedUsernameContent
        ? renderContentWithGeneratedUsername(username, messageType)
        : renderContent(messageType, pointsAwarded)
      }
      <br />
      <Actions>
        <Actions.Button
          primary
          wide
          onClick={handleDoneClick}
          desktop={6}
        >
          {showGeneratedUsernameContent ? 'Not Now' : 'Done'}
        </Actions.Button>
        {messageType === 'price' && !showGeneratedUsernameContent && (
          <Actions.Action desktop={6}>
            <Link to="/contest/gas-card-give-away" uppercase>
              Enter To Win
            </Link>
          </Actions.Action>
        )}
        {showGeneratedUsernameContent && (
          <Actions.Action desktop={6}>
            <Link onClick={onEditUserName} uppercase>
              Edit Username
            </Link>
          </Actions.Action>
        )}
      </Actions>
    </React.Fragment>
  );
}

ThankYou.propTypes = {
  username: PropTypes.string,
  isGeneratedUsername: PropTypes.bool,
  messageType: PropTypes.oneOf(['review', 'price']).isRequired,
  onDone: PropTypes.func,
  onEditUserName: PropTypes.func,
  pointsAwarded: PropTypes.number,
};

ThankYou.defaultProps = {
  username: undefined,
  isGeneratedUsername: false,
  onDone: noop,
  onEditUserName: noop,
  pointsAwarded: undefined,
};
